import PropTypes from "prop-types"
import React, { useEffect, useMemo, useState } from "react"

import { useBreakpointListener } from "../hooks/useBreakpointListener"
import { ColTable } from "./components/ColTable"
import { Table } from "./components/Table"
import { TableContainer as Container } from "./styled"
import { COLLAPSIBLE_COLS, COLLAPSIBLE_ROWS, SCROLLABLE_BOTH, SCROLLABLE_X, SCROLLABLE_Y, tableContext } from "./utils"

export { useTable } from "./hooks/useTable"

const { Provider } = tableContext

/**
 * A flexible data driven grid that can change its presentation.
 *
 * For the selectable behaviour there is the useTable hook. Or adittionaly it is possible to manipulate the props directly.
 */
export function DataTable({
  className,
  loading,
  headers = [],
  data = [],
  sortable,
  sortColumn,
  firstRowIsHeader,
  firstRow,
  onRowClick,
  onCellClick,
  onSelect,
  selectedRows,
  scrollHeight = "30vh",
  responsiveCollapse,
  responsiveScroll,
  scrollable: initialScrolable = "none",
  collapsible: initialCollapsible = "none",
  selectedVariant = "selectOn",
  hoverVariant = "highlight",
  headerVariant = "standard",
  strippedVariant = "standard",
  borderVariant = "standard",
  fixedColumns = [],
  debounceTimeout = 0,
  contentAlignment = "left",
  stripped,
  bordered,
  colLines = true,
  rowLines = true,
  autoFit,
}) {
  const [scrollable, setScrollable] = useState(initialScrolable)
  const [collapsible, setCollapsible] = useState(initialCollapsible)

  useEffect(() => {
    setCollapsible(initialCollapsible)
  }, [initialCollapsible])

  useEffect(() => {
    setScrollable(initialScrolable)
  }, [initialScrolable])

  const selectable = Boolean(onSelect)

  function onWithinBreak({ changed }) {
    if (!changed) {
      return
    }

    if (responsiveCollapse) {
      setCollapsible(COLLAPSIBLE_ROWS)
    }

    if (responsiveScroll) {
      setScrollable(SCROLLABLE_X)
    }
  }

  function onBiggerThanBreak({ changed }) {
    if (!changed) {
      return
    }

    if (responsiveCollapse) {
      setCollapsible(initialCollapsible)
    }

    if (responsiveScroll) {
      setScrollable(initialScrolable)
    }
  }

  useBreakpointListener({
    breakPointName: "medium",
    onWithinBreak,
    onBiggerThanBreak,
    skip: !responsiveCollapse && !responsiveScroll,
    debounceTimeout,
  })
  const { headersToUse, dataToUse } = useMemo(() => {
    let _headers = headers
    let _data = data
    if (!loading && data.length) {
      if (firstRowIsHeader) {
        _headers = data.slice(0, 1)[0]
        _data = data.slice(1)
      }
    }

    return { headersToUse: _headers, dataToUse: _data }
  }, [loading, data, headers, firstRowIsHeader])

  const selectedRowMap = {}

  if (selectedRows) {
    selectedRows.forEach((index) => {
      selectedRowMap[index] = true
    })
  }

  const verticallyScrollable = scrollable === SCROLLABLE_Y || scrollable === SCROLLABLE_BOTH
  const horizontallyScrollable = scrollable === SCROLLABLE_X || scrollable === SCROLLABLE_BOTH

  const numCols = headersToUse.length

  const contextValue = {
    loading,
    numCols,
    headers: headersToUse,
    data: dataToUse,
    firstRow,
    sortable,
    sortColumn,
    onRowClick,
    onCellClick,
    onSelect,
    selectedRowMap,
    collapsible,
    fixedColumns,
    hoverVariant,
    headerVariant,
    strippedVariant,
    selectedVariant,
    borderVariant,
    stripped,
    scrollable,
    bordered,
    colLines,
    rowLines,
    selectable,
    verticallyScrollable,
    horizontallyScrollable,
    autoFit,
  }
  let tableContent = collapsible === COLLAPSIBLE_COLS ? <ColTable /> : <Table />

  return (
    <Provider value={contextValue}>
      <Container
        $contentAlignment={contentAlignment}
        $borderVariant={borderVariant}
        $bordered={bordered}
        $scrollable={scrollable}
        $autoFit={autoFit}
        $colLines={colLines}
        $scrollHeight={scrollHeight}
        $collapsible={collapsible}
        $verticallyScrollable={verticallyScrollable}
        $horizontallyScrollable={horizontallyScrollable}
        className={className}
      >
        {loading && (
          <div role="progressbar" aria-valuetext="Loading.." aria-busy="true">
            Loading...
          </div>
        )}
        {!loading && tableContent}
      </Container>
    </Provider>
  )
}

DataTable.propTypes = {
  /**
   * An array of headers if firstRowIsHeader is false
   */
  headers: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node)]),
  /**
   * The data to render in order
   */
  data: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.node)),
  /**
   * If true the table will show as rendering
   */
  loading: PropTypes.bool,
  /**
   * If true the data can be ordered and reordered
   */
  sortable: PropTypes.bool,
  /**
   * If true data[0] becomes header and data[1:] becomes data
   */
  firstRowIsHeader: PropTypes.bool,
  /**
   * A row that will always be displayed as the first row in the table
   */
  firstRow: PropTypes.arrayOf(PropTypes.node),
  /**
   * The column that is ordered
   */
  sortColumn: PropTypes.string,
  /**
   *  Invoked with ({data:rowData,row: number}, event)
   */
  onRowClick: PropTypes.func,
  /**
   *  Invoked with ({data:cell value,row: number, col:number}, event)
   */
  onCellClick: PropTypes.func,
  /**
   * If true the table will have a border
   */
  bordered: PropTypes.bool,
  /**
   * Display row lines
   * @default true
   */
  rowLines: PropTypes.bool,
  /**
   * Display row lines
   * @default true
   */
  colLines: PropTypes.bool,
  /**
   * If true every other row will be collored
   * @default false
   */
  stripped: PropTypes.bool,

  /**
   * The alignment of the celss
   * @default left
   */
  contentAlignment: PropTypes.oneOf(["left", "right", "center"]),

  /**
   * If true the size of the contents will give the table
   * size
   */
  autoFit: PropTypes.bool,
  /**
   * If true will collapse cols when reach medium breakpoint
   */
  responsiveCollapse: PropTypes.bool,
  /**
   * If true will scroll when reach medium breakpoint
   */
  responsiveScroll: PropTypes.bool,
  /**
   * If true table becomes horizontally scrollable.
   * One of: none, vertically, horizontally, both
   * @default 'none'
   */
  scrollable: PropTypes.oneOf(["none", "vertically", "horizontally", "both"]),
  /**
   * The height if scroll height set
   * @default '30vh'
   */
  scrollHeight: PropTypes.string,
  /**
   * An array of selected rows
   */
  selectedRows: PropTypes.arrayOf(PropTypes.number),
  /**
   * When a row is clicked will be called with ({row,data, selected:bool},event)
   */
  onSelect: PropTypes.func,
  /**
   * How to collapse the table cols,rows or none
   *
   * @default none
   */
  collapsible: PropTypes.oneOf(["none", "cols", "rows"]),

  /**
   * If present will fix columns sizes, put falsy value other than 0 or a value <0 to skip
   */
  fixedColumns: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool])),
  /**
   * One of the defined variants
   *
   * @default selectOn
   */
  selectedVariant: PropTypes.string,
  /**
   * One of the defined variants
   *
   * @default highlight
   */
  hoverVariant: PropTypes.string,
  /**
   * One of the defined variants
   *
   * @default customA
   */
  headerVariant: PropTypes.string,
  /**
   * One of the defined variants
   *
   * @default standard
   */
  strippedVariant: PropTypes.string,
  /**
   * One of the defined variants
   *
   * @default standard
   */
  borderVariant: PropTypes.string,
  /**
   * Timeout of the debouse for responsive behavior
   */
  debounceTimeout: PropTypes.number,
}
